import { useCallback, useEffect, useLayoutEffect, useRef } from "react"
import PropTypes from "prop-types"
import classNames from "classnames"
import Plotly from "plotly.js-dist-min"

const DEFAULT_MARGIN = 50
const MIN_WIDTH_FOR_LEGEND = 300

export default function PlotlyChart({ data, layout, config, className }) {
  const containerRef = useRef(null)
  const isInitialized = useRef(false)

  function isRenderable(container) {
    if (!container || !container.isConnected) {
      return false
    }
    const rect = container.getBoundingClientRect()
    // Guard against zero-size or display:none ancestors
    return rect.width > 0 && rect.height > 0
  }

  const renderPlot = useCallback(async (container) => {
    if (!container || !container.isConnected) {
      return
    }

    if (!isRenderable(container)) {
      requestAnimationFrame(() => {
        if (container && container.isConnected) {
          renderPlot(container)
        }
      })
      return
    }

    const chartLayout = {
      autosize: true,
      margin: {
        l: DEFAULT_MARGIN,
        r: DEFAULT_MARGIN,
        t: DEFAULT_MARGIN,
        b: DEFAULT_MARGIN,
      },
      showlegend: container.offsetWidth > MIN_WIDTH_FOR_LEGEND,
      ...(layout || {}),
    }

    const chartConfig = {
      responsive: true,
      displaylogo: false,
      modeBarButtonsToRemove: ["pan2d", "select2d", "lasso2d", "toImage"],
      ...(config || {}),
    }

    if (!isInitialized.current) {
      await Plotly.newPlot(container, structuredClone(data), structuredClone(chartLayout), structuredClone(chartConfig))
      isInitialized.current = true
    } else {
      await Plotly.react(container, structuredClone(data), structuredClone(chartLayout), structuredClone(chartConfig))
    }
  }, [data, layout, config])

  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container) {
      return
    }

    renderPlot(container)

    return () => {
      try {
        if (container) {
          Plotly.purge(container)
        }
      } catch {
        // no-op on error
      }
      isInitialized.current = false
    }
  }, [data, layout, config, renderPlot])

  useEffect(() => {
    const container = containerRef.current
    if (!container) {
      return
    }

    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(() => {
        if (isInitialized.current && container.isConnected) {
          try {
            Plotly.Plots.resize(container)

            // Show/hide legend based on container width
            const showlegend = container.offsetWidth > MIN_WIDTH_FOR_LEGEND
            if (container.layout?.showlegend !== showlegend) {
              Plotly.relayout(container, {
                ...container.layout,
                showlegend,
              })
            }
          } catch {
          // no-op on error
          }
        }
      })
      ro.observe(containerRef.current)
      return () => ro.disconnect()
    }
  }, [])

  return <div ref={containerRef} className={classNames("plotly-chart", className)} />
}

PlotlyChart.propTypes = {
  data: PropTypes.array.isRequired,
  layout: PropTypes.object.isRequired,
  config: PropTypes.object,
  className: PropTypes.string,
}
